import { orderRows } from "@/types/triageWorklist";
import {
  STATE_TEXT,
  type DevicePanelDevice,
  type DevicePanelFinding,
  type DevicePanelStudyDevice,
  type DevicePanelStudyRow,
} from "@/types/devicePanel";

/**
 * A panel in the notification view, as a site would see it: a worklist of
 * studies and one notification page per study, in the devices' own wording. A
 * cleared device (one with indications) carries its labeling and, where it was
 * not applied, the reason; a device still in clearance has neither.
 */

const distinct = <T>(items: readonly T[]) => [...new Set(items)];

/** The not-applied devices' reasons, each distinct text once; a device without
 * indications gives none. */
const reasons = (devices: readonly { not_applicable_reason: string | null }[]) =>
  distinct(devices.flatMap(({ not_applicable_reason: reason }) => (reason ? [reason] : [])));

/** Whether any device applied to the study flagged a finding at its default. */
export const isFlagged = (row: DevicePanelStudyRow) => row.triage_findings.length > 0;

/** A worklist row's status:
 * - flagged: an applied device flagged a finding;
 * - no_flag: every applied device completed and flagged nothing;
 * - not_sent: no device applies, with the devices' distinct reasons;
 * - unread: nothing flagged yet and an applied device has no readable result,
 *   named by its state ("Running", "Run failed", …). */
export type SiteStatus =
  | { kind: "flagged" }
  | { kind: "no_flag" }
  | { kind: "not_sent"; reasons: string[] }
  | { kind: "unread"; text: string };

export function siteStatus(row: DevicePanelStudyRow): SiteStatus {
  const applied = row.devices.filter((device) => device.applicable);
  if (applied.length === 0) return { kind: "not_sent", reasons: reasons(row.devices) };
  if (isFlagged(row)) return { kind: "flagged" };
  const unread = applied.flatMap(({ state }) => (state === "completed" ? [] : [STATE_TEXT[state]]));
  return unread.length
    ? { kind: "unread", text: distinct(unread).join(" · ") }
    : { kind: "no_flag" };
}

/** The worklist's order: flagged studies first, each group in panel order. */
export const siteOrder = (rows: readonly DevicePanelStudyRow[]) => orderRows(rows, isFlagged, true);

/** Where a study sits among the worklist's `counted` studies (every study by
 * default), in worklist order: its 1-based position (null when it is not
 * counted), how many are counted, and the counted studies just before and
 * after it. */
export function worklistSteps(
  rows: readonly DevicePanelStudyRow[],
  studyUid: string,
  counted: (row: DevicePanelStudyRow) => boolean = () => true,
) {
  const order = siteOrder(rows);
  const at = order.findIndex((row) => row.study_uid === studyUid);
  if (at === -1) throw new Error(`Study ${studyUid} is not on the worklist`);
  const included = order.filter(counted);
  const position = included.indexOf(order[at]);
  return {
    position: position === -1 ? null : position + 1,
    total: included.length,
    previous: order.slice(0, at).filter(counted).at(-1) ?? null,
    next: order.slice(at + 1).find(counted) ?? null,
  };
}

export type WorklistSteps = ReturnType<typeof worklistSteps>;

/** Where a study sits among the flagged studies, as `worklistSteps` reads it. */
export const flaggedSteps = (rows: readonly DevicePanelStudyRow[], studyUid: string) =>
  worklistSteps(rows, studyUid, isFlagged);

/** One device's part of a study's notification:
 * - not_sent: the device was not applied, with the reason when it gives one;
 * - flagged: the findings it flagged at its default;
 * - no_flag: it completed and flagged nothing;
 * - unread: it has no readable result, with its state and message. */
export type DeviceNotice =
  | { kind: "not_sent"; reason: string | null }
  | { kind: "flagged"; findings: DevicePanelFinding[] }
  | { kind: "no_flag" }
  | { kind: "unread"; text: string; message: string | null };

export function deviceNotice(device: DevicePanelStudyDevice): DeviceNotice {
  if (!device.applicable) return { kind: "not_sent", reason: device.not_applicable_reason };
  if (device.state !== "completed")
    return { kind: "unread", text: STATE_TEXT[device.state], message: device.message };
  if (device.findings === null) throw new Error(`Device ${device.key} completed with no findings`);
  const findings = device.findings.filter((finding) => finding.flagged_at_default);
  return findings.length ? { kind: "flagged", findings } : { kind: "no_flag" };
}

/** The cleared devices' clinician notes, each distinct text once, in device order. */
export const clinicianNotes = (devices: readonly DevicePanelDevice[]) =>
  distinct(devices.flatMap(({ labeling }) => (labeling ? [labeling.clinician_note] : [])));
